import { API_BASE_URL } from "../../props";
import { useSelector } from "react-redux";
import { includes, map, pluck, reject } from "ramda";
import { useMutation, useQueryClient } from "react-query";
import {
  doGet,
  doPut,
  useGet,
  useGetWithNext,
  usePost,
  useUpload,
  useDelete,
  usePut,
  useUpdate,
} from "../../common/fetchServices";
import { getQueryParams } from "../../util/fetchUtils";
import { useCurrentOrgId } from "../../auth/authUtils";
import { useDeleteModuleItems } from "../modules/moduleServices";
import { notBlank } from "../../util/ext";

const getCreateAnthologyUrl = () => `${API_BASE_URL}/anthologies`;
const getAnthologyUrl = id => `${API_BASE_URL}/anthologies/${id}`;
const getPatchAnthologyUrl = ({ id }) => `${API_BASE_URL}/anthologies/${id}`;
const getAnthologyCollectionsUrl = id =>
  `${API_BASE_URL}/anthologies/${id}/collections`;
const getAnthologyCollectionsDisplayOrderUpdateUrl = ({ id }) =>
  `${API_BASE_URL}/anthologies/${id}/collections/order`;
const getAnthologyMediaUrl = ({ id }) =>
  `${API_BASE_URL}/anthologies/${id}/media`;
const getAnthologyContentUrl = id =>
  `${API_BASE_URL}/anthologies/${id}/content`;
const getAnthologyContentTypeSummaryUrl = id =>
  `${API_BASE_URL}/anthologies/${id}/content/content-type-summary`;
const getThumbnailUploadUrl = ({ id }) =>
  `${API_BASE_URL}/admin/anthology/${id}/thumbnail`;
const getOrganizationLogoUploadUrl = ({ id }) =>
  `${API_BASE_URL}/admin/anthology/${id}/organization-logo`;

export const useAnthology = id => {
  const orgId = useCurrentOrgId();
  const url = getAnthologyUrl(id);
  return useGet(["anthology", orgId, { id }], url);
};

export const useAnthologyContent = ({ id, offset, limit }) => {
  const url =
    getAnthologyContentUrl(id) + "?" + getQueryParams({ offset, limit });
  return useGet(["anthologyMedia", { id }], url);
};

export const useAnthologyApprovedContent = (id, params) => {
  const orgId = useCurrentOrgId();
  const queryParams = getQueryParams({
    "ct.status": "APPROVED",
    ...params,
  });

  const url = getAnthologyContentUrl(id) + "?" + queryParams;

  return useGet(["anthologyApprovedMedia", orgId, { url }], url);
};

export const useAnthologyCreate = () => {
  return usePost(getCreateAnthologyUrl, {
    queriesToInvadliate: [
      "search",
      "admin-media",
      ["unorganized-content-groups"],
    ],
  });
};

export const useAnthologyUpdate = () => {
  const queryClient = useQueryClient();
  const currentOrgId = useCurrentOrgId();

  return useUpdate(getPatchAnthologyUrl, {
    onSuccess: (data, variables) => {
      const anthology = variables.resourceIds;
      const queries = ["admin-media", "search", ["unorganized-content-groups"]];
      for (const queryKey of queries) {
        queryClient.invalidateQueries(queryKey);
      }
      queryClient.invalidateQueries([
        "anthology",
        currentOrgId,
        { id: anthology.id },
      ]);

      if (notBlank(anthology.parentModules)) {
        anthology.parentModules.forEach(module => {
          queryClient.invalidateQueries([
            "moduleMedia",
            currentOrgId,
            { id: module.id },
          ]);
          queryClient.invalidateQueries([
            "moduleDirectChildren",
            { id: module.id },
          ]);
        });
      }
    },
  });
};

export const useUploadAnthologyThumbnail = () => {
  return useUpload(getThumbnailUploadUrl);
};

export const useUploadAnthologyOrganizationLogo = () => {
  return useUpload(getOrganizationLogoUploadUrl);
};

export const useAddToAnthology = () => {
  return usePost(getAnthologyMediaUrl, {
    queriesToInvadliate: [
      "admin-media",
      ["anthology", ["id"]],
      "anthologyApprovedMedia",
      "anthologyCollections",
      "anthology-collections-count",
      ["anthology-collections-creator"],
      "collection",
      "content-status",
      ["module-all-content"],
      ["module-all-content-count"],
      "search",
      ["unorganized-content-groups"],
    ],
  });
};

export const useRemoveCollectionsFromAnthology = () => {
  const auth = useSelector(state => state.auth);
  const queryClient = useQueryClient();

  return useMutation(
    async ({ anthologyId, mediaIds }) => {
      const anthologyCollectionsUrl =
        getAnthologyCollectionsUrl(anthologyId) + "?limit=all";
      const media = await doGet(auth)(anthologyCollectionsUrl);
      const updated = reject(media => includes(media.id, mediaIds), media);
      const collectionMediaUrl = getAnthologyMediaUrl({ id: anthologyId });
      const body = { ids: pluck("id", updated) };

      return doPut(auth)(collectionMediaUrl, body);
    },
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries("anthologyApprovedMedia");
        queryClient.invalidateQueries("collection-approved-content");
        queryClient.invalidateQueries("anthologyCollections");
        queryClient.invalidateQueries(["anthology-collections-creator"]);
        queryClient.invalidateQueries(["unorganized-content-groups"]);
        queryClient.invalidateQueries(["admin-media"]);
        queryClient.invalidateQueries(["module-content-groups"]);
        variables.mediaIds.forEach(id =>
          queryClient.invalidateQueries(["collection", { id }]),
        );
      },
    },
  );
};

export const useRemoveAllMediaFromAnthology = () => {
  return useDelete(getAnthologyMediaUrl, {
    queriesToInvadliate: [
      "anthologyApprovedMedia",
      "anthologyCollections",
      ["anthology-collections-creator"],
      "collection-approved-content",
      ["unorganized-content-groups"],
    ],
  });
};

export const useAnthologyDelete = () => {
  const queryClient = useQueryClient();
  const updateAnthology = useAnthologyUpdate();
  const removeAllMediaFromAnthology = useRemoveAllMediaFromAnthology();
  const deleteModuleItems = useDeleteModuleItems();

  return useMutation(
    async ({ anthology }) => {
      // Remove all parents and children before "deleting"
      await removeAllMediaFromAnthology.mutateAsync({
        resourceIds: { id: anthology.id },
      });

      if (notBlank(anthology.parentModules)) {
        await Promise.all(
          map(
            module =>
              deleteModuleItems.mutateAsync({
                id: module.id,
                ids: [anthology.id],
              }),
            anthology.parentModules,
          ),
        );
      }

      return updateAnthology.mutateAsync({
        resourceIds: anthology,
        data: {
          status: "DELETED",
        },
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("anthologyApprovedMedia");
        queryClient.invalidateQueries("collection-approved-content");
        queryClient.invalidateQueries("anthologyCollections");
        queryClient.invalidateQueries(["anthology-collections-creator"]);
        queryClient.invalidateQueries(["unorganized-content-groups"]);
        queryClient.invalidateQueries(["module-content-groups"]);
      },
    },
  );
};

export const useAnthologyContentTypeSummary = (id, params) => {
  const orgId = useCurrentOrgId();

  const queryParams = getQueryParams({
    "ct.status": "APPROVED",
    ...params,
  });

  const url = getAnthologyContentTypeSummaryUrl(id) + "?" + queryParams;
  return useGet(["anthology-content-type-summary", orgId, { url }], url);
};

export const useUpdateAnthologyItems = () => {
  const currentOrgId = useCurrentOrgId();

  return usePut(getAnthologyMediaUrl, {
    queriesToInvadliate: [
      "search",
      ["anthology", currentOrgId, ["id"]],
      ["anthologyMedia", ["id"]],
      "moduleMedia",
      ["anthology-content-type-summary", ["id"]],
      ["anthology-collections-count", currentOrgId, ["id"]],
      ["anthologyCollections", currentOrgId, ["id"]],
      "anthologyCollections",
      ["anthology-collections-creator"],
      ["admin-media"],
    ],
  });
};

export const useAnthologyCollections = (id, params = {}, queryOptions = {}) => {
  const orgId = useCurrentOrgId();
  const queryParams = getQueryParams(params);
  const url = getAnthologyCollectionsUrl(id) + "?" + queryParams;
  return useGetWithNext(
    ["anthologyCollections", orgId, { url, id }],
    url,
    {},
    queryOptions,
  );
};

// update === [{"id": collectionId, "displayOrder": newDisplayOrder}, ...]
export const useUpdateAnthologyCollectionOrder = () => {
  const currentOrgId = useCurrentOrgId();
  return useUpdate(getAnthologyCollectionsDisplayOrderUpdateUrl, {
    queriesToInvadliate: [
      ["anthology-collections-creator", currentOrgId, ["id"]],
    ],
  });
};
